import {
  AtlasIconData,
  Button,
  Icon,
  ListItem,
  LoadingIndicator,
  TextField,
} from "@resource/atlas";
import { atlasPlus, atlasSearch } from "@resource/atlas/icons";
import SanitizedTokenizedText from "components/SanitizedTokenizedText";
import _ from "lodash";
import React, {
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { analytics } from "react-hooks/useAnalytics";

type ModalSelectWithPreviewMenuItemProps = {
  icon?: AtlasIconData;
  badge?: React.ReactNode;
  menuItemTitle: string;
  menuItemSubtitle?: string;
  isSelectedItem?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
};

function ModalSelectWithPreviewMenuItem({
  icon,
  badge,
  menuItemTitle,
  menuItemSubtitle,
  isSelectedItem,
  onClick,
}: ModalSelectWithPreviewMenuItemProps) {
  return (
    <ListItem
      isInteractive
      isSelected={isSelectedItem}
      onClick={onClick}
      renderContent={({ children }) => (
        <div className="space-y-1">
          <p className="text-body-md flex">
            {icon && <Icon className="mr-3" content={icon} />}
            <span>
              {children} {badge}
            </span>
          </p>
          {menuItemSubtitle && (
            <SanitizedTokenizedText
              className="text-body-md text-subtle"
              allowedTags={["span", "br", "a", "img"]}
              value={menuItemSubtitle}
            />
          )}
        </div>
      )}
    >
      {menuItemTitle}
    </ListItem>
  );
}

type ModalSelectWithPreviewContentProps<T> = {
  previewItems: T[];
  onCreateNew?: () => void;
  idResolver: (previewItem: T) => string;
  menuItemTitleResolver: (previewItem: T) => string;
  menuItemSubtitleResolver?: (previewItem: T) => string;
  badgeResolver?: (previewItem: T) => React.ReactNode;
  renderPreviewContent: (previewItem: T) => React.ReactNode;
  selectedItem: T | null;
  setSelectedItem: (selectedItem: T | null) => void;
  isLoading: boolean;
};
export default function ModalSelectWithPreviewContent<T>({
  previewItems,
  onCreateNew,
  idResolver,
  menuItemTitleResolver,
  menuItemSubtitleResolver,
  badgeResolver,
  renderPreviewContent,
  selectedItem,
  setSelectedItem,
  isLoading,
}: ModalSelectWithPreviewContentProps<T>) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [searchFilter, setSearchFilter] = useState<string>("");

  useEffect(() => {
    previewRef.current?.scrollTo(0, 0);
  }, [selectedItem]);

  const searchTermNormalized = searchFilter.toLowerCase().trim();
  const itemsFiltered = useMemo(
    () =>
      _(previewItems)
        .filter(
          (item) =>
            !!(
              searchTermNormalized === "" ||
              menuItemTitleResolver(item)
                ?.toLowerCase()
                .includes(searchTermNormalized) ||
              menuItemSubtitleResolver?.(item)
                ?.toLowerCase()
                .includes(searchTermNormalized)
            )
        )
        .uniqBy(idResolver)
        .value(),
    [
      idResolver,
      menuItemTitleResolver,
      menuItemSubtitleResolver,
      previewItems,
      searchTermNormalized,
    ]
  );

  useEffect(() => {
    if (!isLoading) {
      const itemInFiltered =
        selectedItem &&
        itemsFiltered.some((i) => idResolver(i) === idResolver(selectedItem));
      if (!itemInFiltered) {
        setSelectedItem(itemsFiltered[0] ?? null);
      }
    }
  }, [isLoading, itemsFiltered, idResolver, selectedItem, setSelectedItem]);

  return (
    <div className="flex h-full lg:max-h-[60vh] min-h-0">
      <div className="w-64 p-5 overflow-y-auto overflow-x-hidden border-r border-light-gray-600">
        <TextField
          isClearable
          className="mb-4"
          icon={atlasSearch}
          value={searchFilter}
          onChange={(e) => {
            setSearchFilter(e);
          }}
          placeholder="Search templates"
          size="medium"
        />
        {itemsFiltered.map((menuItem) => (
          <ModalSelectWithPreviewMenuItem
            key={idResolver(menuItem)}
            menuItemTitle={menuItemTitleResolver(menuItem)}
            menuItemSubtitle={menuItemSubtitleResolver?.(menuItem)}
            badge={badgeResolver?.(menuItem)}
            isSelectedItem={
              selectedItem
                ? idResolver(menuItem) === idResolver(selectedItem)
                : false
            }
            onClick={() => {
              analytics.track("Template Select Modal Template Clicked");
              setSelectedItem(menuItem);
            }}
          />
        ))}
        {onCreateNew && (
          <ModalSelectWithPreviewMenuItem
            icon={atlasPlus}
            menuItemTitle="Create new template"
            onClick={onCreateNew}
          />
        )}
      </div>
      <div ref={previewRef} className="flex-1 overflow-y-auto">
        {!isLoading && selectedItem && renderPreviewContent(selectedItem)}
        {!isLoading && itemsFiltered.length === 0 && (
          <div className="flex flex-col flex-1 h-full justify-center items-center text-lg min-h-[20rem]">
            <p className="text-h3">No templates available</p>
            <p className="text-body-md text-gray-800 mt-2 max-w-xs">
              Try adjusting your filters to see more results
            </p>
            <Button className="mt-6" onClick={() => setSearchFilter("")}>
              Clear filters
            </Button>
          </div>
        )}
        {isLoading && (
          <div className="flex h-full justify-center items-center">
            <LoadingIndicator />
          </div>
        )}
      </div>
    </div>
  );
}
